/* eslint-disable jsx-a11y/no-static-element-interactions */
import {faMessage, faSearch} from '@fortawesome/pro-solid-svg-icons';
import {Badge, IconButton, useTheme} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import {DialogType} from 'commons/Dialog/CustomDialog';
import CustomIcon from 'commons/Icon/CustomIcon';
import {CustomLink} from 'commons/Link/CustomLink';
import {localize} from 'commons/LocalizedText/LocalizedText';
import BurgerMenu from 'components/Header/BurgerMenu';
import HeaderLeft from 'components/Header/HeaderLeft';
import HeaderMenu from 'components/Header/HeaderMenu';
import {useDialog} from 'hook/DialogProvider';
import {useLaboratoryInfo} from 'hook/UseLaboratoryInfo';
import useMobile from 'hook/UseMobile';
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import SockJsClient from 'react-stomp';
import {ProductTestService} from 'services/ProductTestService';
import {API_URL, PORT, URL} from 'utils/constants/app';
import {ErrorName} from 'utils/constants/error.name';
import {GENERATE_ROUTE, ROUTES} from 'utils/constants/routes';
import {getAssetUrl} from 'utils/helpers/AssetsHelper';
import NotesDisplay from './NotesDisplay';
import SearchBar from './SearchBar';
import {useNoteContext} from "hook/NoteProvider";

const useStyles = makeStyles((theme) => ({
  headerRoot: {
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: theme.palette.shadow.hover
  },
  linkEnd: {
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  imgEnd: {
    height: 60,
    width: 90
  },
  searchDrawer: {
    position: "absolute !important" ,
    top: '75px !important',
    "& .MuiBackdrop-root": {
      position: "absolute"
    },
    "& .MuiDrawer-paper": {
      position: "absolute",
      background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`
    }
  }
}));

const Header = () => {
  const isMobile = useMobile();
  const theme = useTheme();
  const showDialog = useDialog();
  const history = useHistory();
  const laboratoryInfo = useLaboratoryInfo();
  const notesDisplayRef = useRef();
  const notesDisplayButtonRef = useRef();
  const [displayBurger, setDisplayBurger] = useState(false);
  const [displaySearch, setDisplaySearch] = useState(false);
  const [displayNotes, setDisplayNotes] = useState(false);
  const [socketUrl, setSocketUrl] = useState(null);
  const [topic, setTopic] = useState(null);
  const [notificationNumber, setNotificationNumber] = useState(0);
  const [productTestsNotes, setProductTestsNotes] = useState([]);
  const [isLoadingProductTests, setIsLoadingProductTests] = useState(true);
  const { laboratory: laboratoryId } = useParams();
  const [isPendingNoteNeedToRefresh, setIsPendingNoteNeedToRefresh] = useNoteContext();

  const classes = useStyles();

  const getNotes = () => {
    setIsLoadingProductTests(true);
    const promises = [ProductTestService.getProductTestsUnreadNotes(laboratoryId) /*, AuditsService.getAuditsNotes(laboratoryId)*/];
    Promise.all(promises).then(resp => {
      setProductTestsNotes(resp[0]);
      setNotificationNumber(resp.reduce((acc, element) => acc + element.map(e => e.notesCount).reduce((partialSum, a) => partialSum + a, 0), 0));
      setIsLoadingProductTests(false);
    }).catch(errorMessage => {
      if (errorMessage !== ErrorName.ABORT_ERROR) {
        showDialog({
          variant: DialogType.ERROR,
          title: localize('error'),
          description: errorMessage
        });
        history.push(GENERATE_ROUTE(ROUTES.ERROR), { errorMessage: errorMessage });
      }
    });
  }

  useEffect(() => {
    const isDevMode = API_URL.includes("localhost");
    if (isDevMode) {
      setSocketUrl(`${URL}/ws-note/`);
    }else{
      setSocketUrl(`${window.location.origin}:${PORT}/ws-note/`);
    }
    setTopic(`/topic/note/${laboratoryId}`);
    getNotes();
  }, []);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (displayNotes && notesDisplayRef.current && !notesDisplayRef.current.contains(e.target)) {
        if(!notesDisplayButtonRef.current.contains(e.target)){
          setDisplayNotes(false);
        }
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [displayNotes]);

  useEffect(() => {
    if(isPendingNoteNeedToRefresh){
      getNotes();
      setIsPendingNoteNeedToRefresh(false);
    }
  }, [isPendingNoteNeedToRefresh]);

  const handleNoteNotificationRecieve = msg =>{
    getNotes();
    setNotificationNumber(notificationNumber + 1);
  }

  return (
    <>
      <div className={classes.headerRoot}>
        <Drawer onClose={() => setDisplayBurger(false)} open={displayBurger}>
          <BurgerMenu setDisplayBurger={setDisplayBurger} />
        </Drawer>
        <Drawer anchor='top' className={classes.searchDrawer} onClose={() => setDisplaySearch(false)} open={displaySearch}>
          <SearchBar setDisplaySearch={setDisplaySearch}/>
        </Drawer>
        <HeaderLeft laboratoryInfo={laboratoryInfo} onBurgerClick={() => setDisplayBurger(true)} />
        {
          !isMobile && <HeaderMenu setDisplaySearch={setDisplaySearch}/>
        }
        <div style={{position:'relative'}}>
          <IconButton onClick={() => setDisplayNotes((oldState) => ! oldState)} ref={notesDisplayButtonRef}>
            <Badge badgeContent={notificationNumber} color="error" overlap="rectangular">
              <CustomIcon
                color={theme.palette.primary.main}
                icon={faMessage}
                tooltip='notesHeaderTooltip'
              />
            </Badge>
          </IconButton>
          {displayNotes &&
            <div ref={notesDisplayRef}>
              <NotesDisplay
                displayNotes={displayNotes}
                isLoadingProductTests={isLoadingProductTests}
                productTestsNotes={productTestsNotes}
                setDisplayNotes={setDisplayNotes}
              />
            </div>}
        </div>
        <IconButton onClick={() => setDisplaySearch(!displaySearch)}>
          <CustomIcon
            color={theme.palette.primary.main}
            icon={faSearch}
            tooltip='searchHeaderTooltip'
          />
        </IconButton>
        <CustomLink
          className={classes.linkEnd}
          href="https://www.eurovent-certification.com"
          rel="noopener noreferrer"
          target="_blank"
        >
          <img alt="logo" className={classes.imgEnd} src={getAssetUrl("/assets/images/ECC.png")} />
        </CustomLink>
      </div>
      {(socketUrl && topic) &&
      <SockJsClient
        onMessage={handleNoteNotificationRecieve}
        topics={[topic]}
        url={socketUrl}
      />}
    </>
  );
};

export default Header;